import { Component, ElementRef, HostBinding, Input, OnInit } from '@angular/core';
import { LayoutService } from '../../services/layout.service';
import { ConfigService } from '../../config/config.service';
import { map, startWith, switchMap } from 'rxjs/operators';
import { NavigationService } from '../../services/navigation.service';
import { PopoverService } from '../../components/popover/popover.service';
import { MegaMenuComponent } from '../../components/mega-menu/mega-menu.component';
import { Observable, of } from 'rxjs';
import {AuthenticationService} from '../../../app/shared/services/authentication.service';
import {environment} from '../../../environments/environment';
import {AppToolkitModalComponent} from "../../../app/shared/components/app-toolkit-modal/app-toolkit-modal.component";
import {MatDialog} from "@angular/material/dialog";
import {Owner, OwnerService} from '../../../@backend';
import {firstValueFrom} from 'rxjs';
import {OwnerThemeService} from '../../../app/shared/services/owner-theme.service';
import {TenantTransitionService} from '../../../app/shared/services/tenant-transition.service';

const OWNER_LOGO_URLS: Readonly<Record<number, string>> = {
  1: 'assets/img/logos/kodl-logo.svg',
  2: 'assets/img/logos/artslimit-logo.svg',
  3: 'assets/img/logos/contemporary.png',
};

@Component({
  selector: 'vex-toolbar',
  templateUrl: './toolbar.component.html',
  styleUrls: ['./toolbar.component.scss']
})
export class ToolbarComponent implements OnInit {

  @Input() mobileQuery: boolean;
  @Input() collapsed: boolean;

  @Input()
  @HostBinding('class.shadow-b')
  hasShadow: boolean;

  navigationItems = this.navigationService.items;
  apiUrl: string = environment.baseApiUrl;
  frontendUrl: string = '';
  envName: string = environment.name;
  owners: Owner[] = [];


  collapsedOpen$ = this.layoutService.sidenavCollapsedOpen$;
  showCollapsePin$ = this.configService.config$.pipe(map(config => config.sidenav.showCollapsePin));
  isHorizontalLayout$: Observable<boolean> = this.configService.config$.pipe(map(config => config.layout === 'horizontal'));
  isVerticalLayout$: Observable<boolean> = this.configService.config$.pipe(map(config => config.layout === 'vertical'));
  isNavbarInToolbar$: Observable<boolean> = this.configService.config$.pipe(map(config => config.navbar.position === 'in-toolbar'));
  isNavbarBelowToolbar$: Observable<boolean> = this.configService.config$.pipe(map(config => config.navbar.position === 'below-toolbar'));
  userVisible$: Observable<boolean> = this.configService.config$.pipe(map(config => config.toolbar.user.visible));

  megaMenuOpen$: Observable<boolean> = of(false);

  protected readonly environment = environment;

  constructor(private layoutService: LayoutService,
              private configService: ConfigService,
              private navigationService: NavigationService,
              private popoverService: PopoverService,
              public dialog: MatDialog,
              public authService: AuthenticationService,
              private ownerService: OwnerService,
              private ownerThemeService: OwnerThemeService,
              private tenantTransitionService: TenantTransitionService) {
    this.collapsed = false;
  }

  async ngOnInit(): Promise<void> {
    [this.owners] = await Promise.all([
      firstValueFrom(this.ownerService.ownerControllerGetAvailableOwners()),
      this.ownerThemeService.applyCurrentOwnerStyle(),
    ]);
    this.tenantTransitionService.revealIfPending();
  }

  get currentOwner(): Owner | undefined {
    return this.owners.find(owner => owner.id === this.authService.currentOwnerId);
  }

  ownerLogoUrl(owner?: Owner): string | undefined {
    return owner?.id === undefined ? undefined : OWNER_LOGO_URLS[owner.id];
  }

  ownerInitials(owner?: Owner): string {
    const source = owner?.shortcut || owner?.name || '—';
    return source.slice(0, 2).toLocaleUpperCase();
  }

  selectOwner(ownerId?: number): void {
    if(ownerId === undefined) return;
    if(ownerId === this.authService.currentOwnerId) return;
    const currentOwner = this.currentOwner;
    const nextOwner = this.owners.find(owner => owner.id === ownerId);
    if(!currentOwner || !nextOwner) return;
    this.authService.setCurrentOwnerId(ownerId);
    this.tenantTransitionService.start(currentOwner, nextOwner);
  }

  openQuickpanel(): void {
    this.layoutService.openQuickpanel();
  }

  openSidenav(): void {
    this.layoutService.openSidenav();
  }

  openMegaMenu(origin: ElementRef | HTMLElement): void {
    this.megaMenuOpen$ = of(
      this.popoverService.open({
        content: MegaMenuComponent,
        origin,
        offsetY: 12,
        position: [
          {
            originX: 'start',
            originY: 'bottom',
            overlayX: 'start',
            overlayY: 'top'
          },
          {
            originX: 'end',
            originY: 'bottom',
            overlayX: 'end',
            overlayY: 'top',
          },
        ]
      })
    ).pipe(
      switchMap(popoverRef => popoverRef.afterClosed$.pipe(map(() => false))),
      startWith(true),
    );
  }

  openSearch(): void {
    this.layoutService.openSearch();
  }


  toggleCollapse() {
    this.collapsed ? this.layoutService.expandSidenav() : this.layoutService.collapseSidenav();
    this.collapsed = !this.collapsed;
  }




  openToolkitModal() {
    this.dialog.open(AppToolkitModalComponent, {
      width: '70%',
    });
  }
}
